import { gql, useLazyQuery, useMutation } from '@apollo/client'
import { useAuth } from '@nobia/zeus-components/lib/auth'
import {
  clearKitchenConfigurations,
  clearOnboardingResult,
  clearWishlist,
} from '@nobia/zeus-components/lib/sharedState/actions'
import {
  getKitchenConfigurations,
  getOnboardingResult,
  getWishlist,
} from '@nobia/zeus-components/lib/sharedState/selectors'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { KitchenConfigurationFragment } from '../graphql/KitchenConfigurationFragment'
import { OnBoardingFragment } from '../graphql/OnBoardingFragment'
import { ProductFragment } from '../graphql/ProductFragment'

const AddProductsMutation = gql`
  ${ProductFragment}
  mutation AddProducts($input: [String]) {
    customer {
      addProducts(productIds: $input) {
        ...Product
      }
    }
  }
`

const SetOnboardingResultMutation = gql`
  ${OnBoardingFragment}
  mutation SetOnboardinResult($OnBoarding: OnBoardingResultInput!) {
    customer {
      saveOnBoarding(input: $OnBoarding) {
        ...OnBoarding
      }
    }
  }
`

const AddKitchenConfigurationMutation = gql`
  ${KitchenConfigurationFragment}
  mutation AddKitchenConfiguration($input: [CreateKitchenConfigurationInput]) {
    customer {
      addKitchenConfiguration(input: $input) {
        ...KitchenConfiguration
      }
    }
  }
`

const CustomerQuery = gql`
  ${KitchenConfigurationFragment}
  ${ProductFragment}
  ${OnBoardingFragment}
  query CustomerMoreQuery {
    me {
      id
      customer {
        products {
          ...Product
        }
        onBoarding {
          ...OnBoarding
        }
        kitchenConfigurations {
          ...KitchenConfiguration
        }
      }
    }
  }
`

export const useSharedStateSaver = () => {
  const dispatch = useDispatch()
  const wishlist = useSelector(getWishlist)
  const kitchenConfiguration = useSelector(getKitchenConfigurations)
  const onboardingResult = useSelector(getOnboardingResult)
  const [addProducts] = useMutation(AddProductsMutation)
  const [addKitchenConfiguration] = useMutation(AddKitchenConfigurationMutation)
  const [setOnboardingResult] = useMutation(SetOnboardingResultMutation)
  const { status } = useAuth()
  const [loading, setLoading] = useState({
    wishlist: false,
    onboarding: false,
    kitchen: false,
    any: false,
  })
  const [loadCustomer] = useLazyQuery(CustomerQuery)

  const authenticated = status === 'authenticated'

  useEffect(() => {
    if (authenticated && wishlist?.length) {
      setLoading((s) => ({ ...s, wishlist: true, any: true }))
      addProducts({ variables: { input: wishlist.map((p) => p.id) } }).then(
        () => {
          dispatch(clearWishlist())
          setLoading((s) => ({ ...s, wishlist: false }))
        }
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wishlist])

  useEffect(() => {
    if (authenticated && Object.keys(kitchenConfiguration || {}).length > 0) {
      const input = Object.keys(kitchenConfiguration).flatMap((key) =>
        kitchenConfiguration[key].map((c) => c.configurationData)
      )
      setLoading((s) => ({ ...s, kitchen: true, any: true }))
      addKitchenConfiguration({ variables: { input } }).then(() => {
        setLoading((s) => ({ ...s, kitchen: false }))
        dispatch(clearKitchenConfigurations())
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kitchenConfiguration])

  useEffect(() => {
    if (authenticated && Object.keys(onboardingResult || {}).length > 0) {
      delete onboardingResult.createdAt
      onboardingResult.newsletter = onboardingResult.newsletter || false
      setLoading((s) => ({ ...s, onboarding: true, any: true }))
      setOnboardingResult({ variables: { OnBoarding: onboardingResult } }).then(
        () => {
          setLoading((s) => ({ ...s, onboarding: false }))
          dispatch(clearOnboardingResult())
        }
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingResult])

  useEffect(() => {
    if (
      !loading.kitchen &&
      !loading.wishlist &&
      !loading.onboarding &&
      loading.any
    ) {
      loadCustomer({
        fetchPolicy: 'network-only',
      })
    }
  }, [loadCustomer, loading])
}
